/**
 * v3.admin.documentRetentionPolicies — AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25).
 *
 * Admin write surface for the `DocumentRetentionPolicies` table (composite PK
 * `(DocumentCategory, OriginRole)`, default 7 years = 2555 days). Drives the
 * per-row `RetentionEndDate` column on ao_Documents and the nightly retention
 * sweep that hard-deletes expired artifacts where `IsHardHold = false`.
 *
 * Routes (server: PCBJxChangeApi/Functions/AdminDocumentRetentionFunction.cs,
 * landing alongside the WAVE-6.3-BACKEND build):
 *   GET    /v3/admin/document-retention-policies
 *   POST   /v3/admin/document-retention-policies
 *   DELETE /v3/admin/document-retention-policies/{documentCategory}/{originRole}
 *
 * All write routes require the `x-config-seed-bypass: <bypassKey>` header
 * (mirrors v3-admin-cart-retention.ts exactly).
 */

import type {
  RequestOptions,
  DocumentRetentionPolicy,
  DocumentRetentionPolicyUpsertInput,
  AoDocumentCategory,
  CartOriginRole,
  ApiResult,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import type { AdminWriteResult } from './admin.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

class DocumentRetentionPoliciesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** Read endpoint is admin-scoped but does not require the bypass key. */
  public list(opts?: RequestOptions): Promise<ApiResult<DocumentRetentionPolicy[]>> {
    return this.http.get<ApiResult<DocumentRetentionPolicy[]>>(
      '/v3/admin/document-retention-policies',
      { ...opts },
    );
  }

  public upsert(
    bypassKey: string,
    body: DocumentRetentionPolicyUpsertInput,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/document-retention-policies', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    documentCategory: AoDocumentCategory,
    originRole: CartOriginRole,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path =
      `/v3/admin/document-retention-policies/${encodeURIComponent(documentCategory)}/${encodeURIComponent(originRole)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

/** Composite — exposed at `client.retail.admin.documentRetentionPolicies`. */
export class AdminDocumentRetentionApi {
  public readonly policies: DocumentRetentionPoliciesAdminApi;

  public constructor(http: ApiHttpClient) {
    this.policies = new DocumentRetentionPoliciesAdminApi(http);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.documentRetentionPolicies`.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** AO-DOCUMENTS-SCHEMA-REBUILD — DocumentRetentionPolicies typed write surface. */
    readonly documentRetentionPolicies: AdminDocumentRetentionApi;
  }
}
